import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { asset } from '../assets';
import { useAuth } from './AuthProvider';
import { useLanguage } from './LanguageProvider';
import { useQuoteCart } from './QuoteCartProvider';

const navItems = [
  ['offers', 'Offers', 'العروض'],
  ['products', 'Products', 'المنتجات'],
  ['services', 'About Us', 'من نحن'],
  ['clients', 'Clients', 'العملاء'],
  ['quote', 'Contact', 'اتصل بنا'],
];

const searchableProducts = [
  ['/business-cards', 'Business Cards', 'بطاقات العمل', 'cards networking'],
  ['/flyer-product', 'Flyers', 'النشرات', 'flyer leaflet'],
  ['/leaflets', 'Leaflets', 'المنشورات المطوية', 'folded flyers'],
  ['/invitations', 'Invitations', 'الدعوات', 'invitation cards'],
  ['/brochures', 'Brochures', 'الكتيبات', 'brochure'],
  ['/letterheads', 'Letterheads', 'أوراق رسمية', 'stationery'],
  ['/catalogues', 'Catalogues', 'الكتالوجات', 'catalogs lookbooks'],
  ['/menus', 'Menus', 'القوائم', 'restaurant menu'],
  ['/postcards', 'Postcards', 'البطاقات البريدية', 'post cards'],
  ['/envelopes-ready-made', 'Envelopes (Ready Made)', 'المظاريف (جاهزة)', 'envelope'],
  ['/envelopes-customized', 'Envelopes (Customized)', 'المظاريف (مخصصة)', 'envelope'],
  ['/folders', 'Folders', 'المجلدات', 'presentation folder'],
  ['/notebooks', 'Notebooks', 'المفكرات', 'books notebook'],
  ['/certificates', 'Certificates', 'الشهادات', 'certificate'],
  ['/badges', 'Badges / Name Tags', 'الشارات / بطاقات الأسماء', 'badge name tag'],
  ['/prescription-pads', 'Prescription Pads', 'دفاتر الوصفات الطبية', 'medical'],
  ['/id-cards', 'ID Cards', 'بطاقات الهوية', 'identity cards'],
  ['/self-carbon-notebooks', 'Self-Carbon Notebooks', 'دفاتر الكربون الذاتي', 'ncr carbonless'],
  ['/bookmarks', 'Bookmarks', 'علامات الكتب', 'bookmark'],
  ['/kraft-paper-bags', 'Kraft Paper Bags', 'أكياس ورق كرافت', 'packaging bags'],
  ['/coated-paper-bags', 'Coated Paper Bags', 'أكياس ورق مطلي', 'packaging bags'],
  ['/calendars', 'Calendars', 'التقويمات', 'calendar'],
];

export function SiteHeader({ home = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const { openLogin, user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { cart, openCart } = useQuoteCart();
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return searchableProducts.slice(0, 6);

    return searchableProducts.filter(([, english, arabic, aliases]) => (
      `${english} ${arabic} ${aliases}`.toLocaleLowerCase().includes(query)
    )).slice(0, 8);
  }, [searchQuery]);

  useEffect(() => {
    if (!searchOpen) return undefined;

    searchInputRef.current?.focus();
    const closeSearch = (event) => {
      if (event.key === 'Escape' || !searchRef.current?.contains(event.target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('keydown', closeSearch);
    document.addEventListener('pointerdown', closeSearch);
    return () => {
      document.removeEventListener('keydown', closeSearch);
      document.removeEventListener('pointerdown', closeSearch);
    };
  }, [searchOpen]);

  const submitSearch = (event) => {
    event.preventDefault();
    if (!searchResults.length) return;
    navigate(searchResults[0][0]);
    setSearchOpen(false);
    setSearchQuery('');
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  return (
    <header>
      <div className="top_header">
        <div className="lang_search" ref={searchRef}>
          <button
            aria-expanded={searchOpen}
            aria-haspopup="dialog"
            className="header-search-trigger"
            onClick={() => setSearchOpen((open) => !open)}
            type="button"
          >
            <img src={asset('assets/search.svg')} alt="" />
            <span>{t('Search', 'بحث')}</span>
          </button>
          <span aria-hidden="true">|</span>
          <button className={language === 'en' ? 'en active' : 'en'} onClick={() => setLanguage('en')} type="button">En</button> /
          <button className={language === 'ar' ? 'ar active' : 'ar'} onClick={() => setLanguage('ar')} type="button">Ar</button>
          {searchOpen && (
            <div aria-label={t('Search products', 'البحث عن المنتجات')} className="header-search-panel" role="dialog">
              <form onSubmit={submitSearch}>
                <img aria-hidden="true" src={asset('assets/search.svg')} alt="" />
                <input
                  aria-label={t('Search products', 'البحث عن المنتجات')}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('Search products...', 'ابحث عن المنتجات...')}
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                />
              </form>
              <div className="header-search-results">
                {searchResults.length > 0 ? searchResults.map(([path, english, arabic]) => (
                  <Link key={path} onClick={closeSearch} to={path}>
                    <span>{language === 'ar' ? arabic : english}</span>
                    <small>{language === 'ar' ? english : arabic}</small>
                  </Link>
                )) : (
                  <p>{t('No products found.', 'لم يتم العثور على منتجات.')}</p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="phone"><img src={asset('assets/phone-number.png')} alt="" /></div>
      </div>
      <nav className="bottom_header">
        <Link className="header-logo" to="/"><img className="logo" src={asset('assets/logo.png')} alt="INGAZ Print" /></Link>
        <button
          aria-expanded={menuOpen}
          aria-label={menuOpen ? t('Close menu', 'إغلاق القائمة') : t('Open menu', 'فتح القائمة')}
          className={`burger-menu${menuOpen ? ' active' : ''}`}
          onClick={() => setMenuOpen((open) => !open)}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
        <ul className={`nav-menu${menuOpen ? ' active' : ''}`}>
          {navItems.map(([id, english, arabic]) => (
            <li key={id}>
              <a href={`${home ? '' : '/'}#${id}`} onClick={() => setMenuOpen(false)}>{t(english, arabic)}</a>
            </li>
          ))}
        </ul>
        <div className="header-actions">
          <button aria-label={t('Open quote cart', 'فتح سلة عروض الأسعار')} className="btn-cart" onClick={openCart} type="button">
            <img aria-hidden="true" src={asset('assets/cart.svg')} alt="" />
            {cart.length > 0 && <span>{cart.length}</span>}
          </button>
          {user ? (
            <Link className="btn-profile-icon" to="/profile" aria-label="My Profile">
              <img src={asset('assets/profile-icon.svg')} alt="" />
            </Link>
          ) : (
            <button className="btn-auth" onClick={openLogin} type="button">{t('Login', 'تسجيل الدخول')}</button>
          )}
          <a href={`${home ? '' : '/'}#products`} className="btn1" style={{ textDecoration: 'none' }}>
            {t('Get a Quote', 'احصل على عرض أسعار')}
          </a>
        </div>
      </nav>
    </header>
  );
}
